import React, { useRef } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PublicLayout from '../components/PublicLayout';
import {
  AboutBuilding,
  AboutMission,
  AboutTeam,
  AboutTimeline,
  Eyebrow,
  HomeCta,
  useHeroParallax,
  useMarketingScroll,
} from '../components/marketing';
import { marketingLinks } from '../constants/marketingEvent';
import { marketingColor, marketingLayout, marketingLine } from '../theme/marketingTokens';

/**
 * About page, following the marketing handoff: hero with a cursor-following
 * glow → mission, the Mozi naming story and principles → what we're building →
 * leadership → scroll-revealed journey → CTA.
 */
export const AboutPage: React.FC = () => {
  const { t } = useTranslation() as { t: (key: string) => string };
  const parallax = useHeroParallax();
  const timelineRef = useRef<HTMLDivElement | null>(null);

  const { timelineProgress } = useMarketingScroll({
    timeline: timelineRef,
  });

  const bookDemo = () => window.open(marketingLinks.scheduler, '_blank', 'noopener,noreferrer');

  return (
    <PublicLayout>
      <Box>
        <Box
          component="section"
          onMouseMove={parallax.onMouseMove}
          onMouseLeave={parallax.onMouseLeave}
          sx={{
            position: 'relative',
            px: marketingLayout.sectionPaddingX,
            pt: { xs: 7, md: 12 },
            pb: { xs: 6, md: 9.5 },
            textAlign: 'center',
            borderBottom: `1px solid ${marketingLine.hairline}`,
            overflow: 'hidden',
          }}
        >
          <Box
            aria-hidden
            sx={{
              position: 'absolute',
              width: 480,
              height: 480,
              left: '50%',
              top: 0,
              ml: '-240px',
              borderRadius: '999px',
              background:
                'radial-gradient(circle, rgba(95,226,138,0.14) 0%, transparent 65%)',
              transform: parallax.layer(40),
              transition: parallax.transition,
              pointerEvents: 'none',
            }}
          />
          <Box sx={{ position: 'relative' }}>
            <Eyebrow sx={{ mb: 3.25 }}>{t('public.about.hero.eyebrow')}</Eyebrow>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.25rem', md: '2.75rem' },
                maxWidth: 740,
                mx: 'auto',
                mb: 2.5,
              }}
            >
              {t('public.about.hero.title')}
            </Typography>
            <Typography
              sx={{
                fontSize: 15.5,
                color: marketingColor.textSecondary,
                lineHeight: 1.65,
                maxWidth: 640,
                mx: 'auto',
              }}
            >
              {t('public.about.hero.subtitle')}
            </Typography>
          </Box>
        </Box>

        <AboutMission />
        <AboutBuilding />
        <AboutTeam />
        <AboutTimeline sectionRef={timelineRef} progress={timelineProgress} />

        <Box sx={{ borderTop: `1px solid ${marketingLine.hairline}` }}>
          <HomeCta onBookDemo={bookDemo} copyKey="public.about.cta" />
        </Box>
      </Box>
    </PublicLayout>
  );
};

export default AboutPage;
